import { type TFunction } from 'i18next';
import {
  CHILLDOC_FORMAT,
  CHILLDOC_MIME,
  type ChilldocEnvelope,
  type DecisionAnswerView,
  type DecisionExportFormat,
  type DecisionQuestionView,
  type DecisionSheetView,
  writeChilldoc,
} from '@apm/contracts';
import { decisionSections, decisionTotals, optionTitle, stampDateTimeIn } from './model';

/**
 * "Xuất thành tài liệu" — APM-DSHEET-e, plan §6.1. The pure half: sheet view ⇒
 * `.chilldoc` envelope (TipTap JSON). Built on the **web** because the server
 * has no TipTap; the upload/document half lives in `export-flow.ts`.
 *
 * Layout: title (H1), one line "exported at dd/MM/yyyy HH:mm · n/N decided",
 * then one H2 per group (code + title, group note below) followed by a table of
 * its questions — a table rather than prose because the reader scans it.
 * Ungrouped questions come last (same order as the sheet page,
 * `decisionSections`). An unanswered question still gets its row, with
 * "Chưa chốt" in the answer column: the export must show what is still open.
 */

export interface SheetDocLabels {
  /** Line under the title: export time and progress. */
  readonly meta: (at: string, answered: number, total: number) => string;
  /** Code · Question · Answer · Recommended · Note · Decided by · At */
  readonly columns: readonly [string, string, string, string, string, string, string];
  readonly unanswered: string;
  readonly other: string;
  readonly ungrouped: string;
  /** "Decided by" of an answer that came in with an imported file. */
  readonly imported: string;
  /** "Decided by" of an outsider who answered via a public link: "Nguyễn A (a@x.com, qua link)". */
  readonly respondent: (name: string, email: string) => string;
}

export function sheetDocLabels(t: TFunction): SheetDocLabels {
  return {
    meta: (at, answered, total) => t('decisionSheet.exportDoc.meta', { at, answered, total }),
    columns: [
      t('decisionSheet.exportDoc.columns.code'),
      t('decisionSheet.exportDoc.columns.question'),
      t('decisionSheet.exportDoc.columns.answer'),
      t('decisionSheet.exportDoc.columns.recommended'),
      t('decisionSheet.exportDoc.columns.note'),
      t('decisionSheet.exportDoc.columns.decidedBy'),
      t('decisionSheet.exportDoc.columns.at'),
    ],
    unanswered: t('decisionSheet.questionStatus.open'),
    other: t('decisionSheet.other'),
    ungrouped: t('decisionSheet.ungrouped'),
    imported: t('decisionSheet.historyImported'),
    respondent: (name, email) => t('decisionSheet.respondent.decidedBy', { name, email }),
  };
}

type PmNode = Record<string, unknown>;

/** ProseMirror rejects empty text nodes, so an empty string is an empty paragraph. */
function paragraph(text: string): PmNode {
  const lines = text.split(/\r?\n/);
  const content: PmNode[] = [];
  lines.forEach((line, index) => {
    if (index > 0) content.push({ type: 'hardBreak' });
    if (line) content.push({ type: 'text', text: line });
  });
  // Trailing/only hard breaks of an all-blank text are noise.
  return text.trim() ? { type: 'paragraph', content } : { type: 'paragraph' };
}

function heading(level: 1 | 2, text: string): PmNode {
  return { type: 'heading', attrs: { level }, content: [{ type: 'text', text }] };
}

function cell(type: 'tableHeader' | 'tableCell', text: string): PmNode {
  return {
    type,
    attrs: { colspan: 1, rowspan: 1, colwidth: null },
    content: [paragraph(text)],
  };
}

function row(type: 'tableHeader' | 'tableCell', cells: readonly string[]): PmNode {
  return { type: 'tableRow', content: cells.map((text) => cell(type, text)) };
}

function optionLabel(question: DecisionQuestionView, key: string, labels: SheetDocLabels): string {
  return `${key}. ${optionTitle(question, key, labels.other)}`;
}

/** A link answer from a signed-in user still names the link: that is where it came from. */
function decidedBy(answer: DecisionAnswerView, labels: SheetDocLabels): string {
  if (answer.respondent) return labels.respondent(answer.respondent.name, answer.respondent.email);
  return answer.answeredBy?.name ?? (answer.imported ? labels.imported : '');
}

function questionRow(
  question: DecisionQuestionView,
  timezone: string,
  labels: SheetDocLabels,
): readonly string[] {
  const recommended = question.options.find((option) => option.recommended === true);
  const answer = question.answer;
  return [
    question.code,
    question.title,
    answer ? optionLabel(question, answer.key, labels) : labels.unanswered,
    recommended ? optionLabel(question, recommended.key, labels) : '',
    answer?.note.trim() ?? '',
    answer ? decidedBy(answer, labels) : '',
    answer ? stampDateTimeIn(answer.answeredAt, timezone) : '',
  ];
}

/**
 * Sheet ⇒ `.chilldoc` envelope. `now` and `timezone` are parameters (not
 * `new Date()` / `orgTimezone()`) so the output is deterministic under test and
 * every time in the document is in the **organisation's** timezone.
 */
export function sheetToChilldoc(
  view: Pick<DecisionSheetView, 'title' | 'description' | 'groups' | 'questions'>,
  now: Date,
  timezone: string,
  labels: SheetDocLabels,
): ChilldocEnvelope {
  const totals = decisionTotals(view.questions);
  const content: PmNode[] = [
    heading(1, view.title),
    paragraph(labels.meta(stampDateTimeIn(now, timezone), totals.answered, totals.total)),
  ];
  if (view.description?.trim()) content.push(paragraph(view.description));

  // A sheet with no groups at all needs no "Ungrouped" heading over its only table.
  const hasGroups = view.groups.length > 0;
  for (const section of decisionSections(view, 'all')) {
    if (section.group) {
      content.push(heading(2, `${section.group.code}. ${section.group.title}`));
      if (section.group.note?.trim()) content.push(paragraph(section.group.note));
    } else if (hasGroups) {
      content.push(heading(2, labels.ungrouped));
    }
    content.push({
      type: 'table',
      content: [
        row('tableHeader', labels.columns),
        ...section.questions.map((question) =>
          row('tableCell', questionRow(question, timezone, labels)),
        ),
      ],
    });
  }

  return {
    format: CHILLDOC_FORMAT,
    // `writeChilldoc` picks the stored version from the content.
    version: 1,
    fonts: [],
    doc: { type: 'doc', content },
  };
}

/** Filename of the exported file — the sheet title, without path separators. */
export function sheetDocFilename(title: string, extension: 'chilldoc' | 'md' = 'chilldoc'): string {
  const base = title
    .replace(/[\\/:*?"<>|]+/g, ' ')
    // Collapses the separators above and control characters (newline, tab) alike.
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 150)
    .trim();
  return `${base || 'decision-sheet'}.${extension}`;
}

/** The `.chilldoc` file ready for `uploadFile` (presign → PUT to S3 → commit, rule #3). */
export function sheetToChilldocFile(
  view: Pick<DecisionSheetView, 'title' | 'description' | 'groups' | 'questions'>,
  now: Date,
  timezone: string,
  labels: SheetDocLabels,
): File {
  const text = writeChilldoc(sheetToChilldoc(view, now, timezone, labels));
  return new File([text], sheetDocFilename(view.title), { type: CHILLDOC_MIME });
}

// ── Markdown — APM-DSX ──────────────────────────────────────────────────────

/** MIME of the `.md` export — listed in `ALLOWED_MIME_TYPES`. */
export const SHEET_MARKDOWN_MIME = 'text/markdown';

/**
 * One GFM table cell: `|` would end the cell and a newline the row, so they
 * become `\|` and `<br>` (GFM renders inline HTML `<br>` inside a cell).
 */
function markdownCell(text: string): string {
  return text.trim().replace(/\|/g, '\\|').replace(/\r?\n/g, '<br>');
}

/**
 * A free-text block (description, group note): blank lines split paragraphs,
 * single newlines stay line breaks (`\` at the end of the line, CommonMark's
 * hard break) — the author's line breaks are part of the text.
 */
function markdownParagraphs(text: string): string {
  return text
    .trim()
    .split(/\r?\n\s*\r?\n/)
    .map((paragraph) =>
      paragraph
        .split(/\r?\n/)
        .map((line) => line.trim())
        .join('\\\n'),
    )
    .join('\n\n');
}

/** A heading is one line: a newline would end it mid-title. */
function markdownLine(text: string): string {
  return text.replace(/\s*\r?\n\s*/g, ' ').trim();
}

function markdownTable(rows: readonly (readonly string[])[], columns: readonly string[]): string {
  const line = (cells: readonly string[]): string => `| ${cells.map(markdownCell).join(' | ')} |`;
  const divider = `| ${columns.map(() => '---').join(' | ')} |`;
  return [line(columns), divider, ...rows.map(line)].join('\n');
}

/**
 * Sheet ⇒ Markdown — the same document as `sheetToChilldoc`, for readers
 * outside the app (Git, wiki, an AI prompt): `# title`, the meta line, the
 * description; per group `## <code>. <title>` with its highlight (italic) and
 * note, then a GFM table of the same seven columns. Ungrouped questions come
 * last. `now` / `timezone` are parameters for the same reason as there.
 */
export function sheetToMarkdown(
  view: Pick<DecisionSheetView, 'title' | 'description' | 'groups' | 'questions'>,
  now: Date,
  timezone: string,
  labels: SheetDocLabels,
): string {
  const totals = decisionTotals(view.questions);
  const blocks: string[] = [
    `# ${markdownLine(view.title)}`,
    labels.meta(stampDateTimeIn(now, timezone), totals.answered, totals.total),
  ];
  if (view.description?.trim()) blocks.push(markdownParagraphs(view.description));

  // Same rule as the `.chilldoc`: no "Ungrouped" heading when there are no groups.
  const hasGroups = view.groups.length > 0;
  for (const section of decisionSections(view, 'all')) {
    if (section.group) {
      blocks.push(`## ${markdownLine(`${section.group.code}. ${section.group.title}`)}`);
      const highlight = section.group.highlight?.trim();
      if (highlight) blocks.push(`*${markdownLine(highlight).replace(/\*/g, '\\*')}*`);
      if (section.group.note?.trim()) blocks.push(markdownParagraphs(section.group.note));
    } else if (hasGroups) {
      blocks.push(`## ${markdownLine(labels.ungrouped)}`);
    }
    blocks.push(
      markdownTable(
        section.questions.map((question) => questionRow(question, timezone, labels)),
        labels.columns,
      ),
    );
  }
  return `${blocks.join('\n\n')}\n`;
}

/** The `.md` file ready for `uploadFile` — same base name as the `.chilldoc`. */
export function sheetToMarkdownFile(
  view: Pick<DecisionSheetView, 'title' | 'description' | 'groups' | 'questions'>,
  now: Date,
  timezone: string,
  labels: SheetDocLabels,
): File {
  const text = sheetToMarkdown(view, now, timezone, labels);
  return new File([text], sheetDocFilename(view.title, 'md'), { type: SHEET_MARKDOWN_MIME });
}

/** The export file of either format (APM-DSX) — what `exportSheetToDocument` uploads. */
export function sheetToExportFile(
  format: DecisionExportFormat,
  view: Pick<DecisionSheetView, 'title' | 'description' | 'groups' | 'questions'>,
  now: Date,
  timezone: string,
  labels: SheetDocLabels,
): File {
  return format === 'md'
    ? sheetToMarkdownFile(view, now, timezone, labels)
    : sheetToChilldocFile(view, now, timezone, labels);
}
